import { useMemo, useState } from 'react';
import { classNames } from '@scripts/lib/formatters';

/**
 * Mount target:
 *   <div data-island="account-widget" data-props="{...}"></div>
 *
 * Props contract:
 * - title: string
 * - greeting: string
 * - overviewLabel: string
 * - ordersLabel: string
 * - supportLabel: string
 * - customerName?: string
 * - summary: Array<{ label: string, value: string }>
 * - orders: Array<{ number: string, status: string, total: string, url?: string }>
 * - supportLinks: Array<{ label: string, url: string }>
 *
 * Trigger conditions:
 * - Render this island inside the account dashboard header or sidebar when the
 *   theme needs quick account context without replacing the full My Account flow.
 */
export function AccountWidgetIsland({
  title = 'Account Snapshot',
  greeting = 'Welcome back.',
  overviewLabel = 'Overview',
  ordersLabel = 'Orders',
  supportLabel = 'Support',
  noSummaryLabel = 'No account summary is available yet.',
  noOrdersLabel = 'No recent orders are available.',
  noSupportLabel = 'No support shortcuts are available.',
  viewOrderLabel = 'View Order',
  customerName = '',
  summary = [],
  orders = [],
  supportLinks = [],
}) {
  const tabs = useMemo(
    () => [
      { key: 'overview', label: overviewLabel },
      { key: 'orders', label: ordersLabel },
      { key: 'support', label: supportLabel },
    ],
    [ordersLabel, overviewLabel, supportLabel]
  );
  const [activeTab, setActiveTab] = useState('overview');

  return (
    <section className="island-panel space-y-5">
      <header className="space-y-2">
        <h3 className="text-lg font-semibold text-slate-900">{title}</h3>
        <p className="text-sm text-slate-500">
          {greeting}
          {customerName ? ` ${customerName}` : ''}
        </p>
      </header>

      <nav aria-label={title} className="flex flex-wrap gap-2">
        {tabs.map((tab) => (
          <button
            key={tab.key}
            type="button"
            className={classNames(
              'rounded-full border px-4 py-2 text-sm font-medium transition',
              activeTab === tab.key
                ? 'border-brand-600 bg-brand-600 text-white'
                : 'border-slate-300 bg-white text-slate-700 hover:border-brand-300 hover:text-brand-700'
            )}
            onClick={() => setActiveTab(tab.key)}
          >
            {tab.label}
          </button>
        ))}
      </nav>

      {activeTab === 'overview' ? (
        <div className="grid gap-3 sm:grid-cols-2">
          {summary.length > 0 ? (
            summary.map((item) => (
              <article key={item.label} className="rounded-2xl border border-slate-200 p-4">
                <p className="text-xs font-semibold uppercase tracking-[0.18em] text-slate-500">{item.label}</p>
                <p className="mt-2 text-lg font-semibold text-slate-900">{item.value}</p>
              </article>
            ))
          ) : (
            <p className="text-sm text-slate-500">{noSummaryLabel}</p>
          )}
        </div>
      ) : null}

      {activeTab === 'orders' ? (
        <div className="space-y-3">
          {orders.length > 0 ? (
            orders.map((order) => (
              <article key={order.number} className="rounded-2xl border border-slate-200 p-4">
                <div className="flex flex-wrap items-center justify-between gap-3">
                  <div className="space-y-1">
                    <p className="text-sm font-semibold text-slate-900">#{order.number}</p>
                    <p className="text-sm text-slate-500">{order.status}</p>
                  </div>
                  <div className="text-right">
                    <p className="text-sm font-medium text-slate-900">{order.total}</p>
                    {order.url ? (
                      <a href={order.url} className="text-sm text-brand-700 hover:text-brand-800">
                        {viewOrderLabel}
                      </a>
                    ) : null}
                  </div>
                </div>
              </article>
            ))
          ) : (
            <p className="text-sm text-slate-500">{noOrdersLabel}</p>
          )}
        </div>
      ) : null}

      {activeTab === 'support' ? (
        <div className="space-y-3">
          {supportLinks.length > 0 ? (
            supportLinks.map((item) => (
              <a
                key={item.label}
                href={item.url}
                className="flex items-center justify-between gap-3 rounded-2xl border border-slate-200 px-4 py-4 text-sm font-medium text-slate-700 transition hover:border-brand-300 hover:text-brand-700"
              >
                <span>{item.label}</span>
                <span aria-hidden="true">&rarr;</span>
              </a>
            ))
          ) : (
            <p className="text-sm text-slate-500">{noSupportLabel}</p>
          )}
        </div>
      ) : null}
    </section>
  );
}
